/**
 * EventTracker — universal product analytics for the portal.
 *
 * ─── Universal event model ───────────────────────────────────────────────
 * Every event in the system has the same shape:
 *
 *     {
 *       event_id:    string       (client UUID, dedupe key)
 *       event_type:  string       ("dashboard.viewed", "button.clicked", ...)
 *       ts_ms:       number       (client clock)
 *       entity_id?:  string       (polymorphic subject — the thing the event is about)
 *       properties?: {            (flat scalar bag for typed context)
 *         filter_name?: string,
 *         chart_type?:  string,
 *         ...any scalar key (validated + sanitized server-side)
 *       }
 *     }
 *
 * Server enriches with user_id/org_id/env_id from the session. Writes to
 * analytics.portal_events partitioned by event_hour, queryable via DSL.
 *
 * One method. Any UI element, any backend event, any customer interaction —
 * all go through the same shape. No schema migrations, no column sprawl.
 *
 * ─── Non-blocking contract ───────────────────────────────────────────────
 *  1. Never blocks the UI. Every track() call returns synchronously after
 *     a cheap in-memory push. Flushing is scheduled async, via
 *     requestIdleCallback where available, setTimeout otherwise.
 *
 *  2. Never throws. Every fetch failure, network drop, parse error, or
 *     unexpected runtime error is swallowed. Analytics must never crash
 *     a user-facing page.
 *
 *  3. Fire-and-forget on unload. Flushes on `visibilitychange` (hidden)
 *     and `beforeunload`, using `fetch({ keepalive: true })` so events
 *     survive page navigation.
 *
 *  4. Zero UX latency. The POST is never awaited on a user interaction
 *     path. Even on init(), the flush loop runs in the background.
 */
import { config } from '@/lib/config';

/**
 * Property values that are safe to send to the backend. Scalars or arrays
 * of scalars. Server also validates + sanitizes, but we keep the type
 * tight on the client so call sites can't accidentally pass in objects.
 */
type PropertyValue = string | number | boolean | null | Array<string | number | boolean | null>;

export type EventProperties = Record<string, PropertyValue>;

/**
 * Options for a tracker.track() call.
 */
export type TrackOptions = {
  /** Polymorphic subject of the event (dashboard_id, notebook_id, button_id, ...) */
  entity_id?: string;
  /** Optional duration (for events that measure time, e.g. page-time-on-screen). */
  duration_ms?: number;
  /** Optional category grouping (e.g. "interaction", "nav", "error"). */
  event_category?: string;
  /** Free-form scalar bag. Validated + sanitized server-side via allowlist. */
  properties?: EventProperties;
};

/**
 * What lives in the in-memory buffer. Mirrors the universal schema exactly
 * so the POST body is just JSON.stringify(buffered).
 */
type BufferedEvent = {
  event_id: string;
  event_type: string;
  ts_ms: number;
  entity_id?: string;
  duration_ms?: number;
  event_category?: string;
  properties?: EventProperties;
};

// Absolute URL to the API — portal.solidafy.com serves static assets from a
// CDN, so a relative URL would POST back to the CDN and return 405. We must
// use the API base URL that the rest of the frontend uses for API calls.
const ENDPOINT = `${config.apiUrl}/api/v1/portal-events`;
const MAX_BATCH_SIZE = 50;
const MAX_BATCH_AGE_MS = 30_000;
const MAX_BUFFER_SIZE = 500; // hard cap — drop oldest if exceeded

type IdleCallbackHandle = number;
type IdleRequestCallback = (deadline: { didTimeout: boolean; timeRemaining(): number }) => void;
type WindowWithIdleCallback = Window & {
  requestIdleCallback?: (cb: IdleRequestCallback, opts?: { timeout: number }) => IdleCallbackHandle;
};

/** Schedule a callback during browser idle time (or fall back to setTimeout). */
function scheduleIdle(cb: () => void): void {
  try {
    const w = window as WindowWithIdleCallback;
    if (typeof w.requestIdleCallback === 'function') {
      w.requestIdleCallback(() => cb(), { timeout: 200 });
      return;
    }
  } catch {
    // ignore
  }
  setTimeout(cb, 0);
}

/** Generate a client-side UUID using `crypto.randomUUID()` with a fallback. */
function generateEventId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    // ignore
  }
  return `evt_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

class EventTracker {
  private buffer: BufferedEvent[] = [];
  private flushTimer: ReturnType<typeof setTimeout> | null = null;
  /** Guard so bursty track() calls only enqueue one pending idle flush. */
  private idleFlushPending = false;
  private initialized = false;

  /**
   * Record an event. Synchronous. Never awaits, never throws.
   *
   * Examples:
   *   tracker.track('dashboard.viewed', { entity_id: dashboard.id });
   *
   *   tracker.track('dashboard.filter-applied', {
   *     entity_id: dashboard.id,
   *     properties: { filter_name: 'region', filter_type: 'multi_select' },
   *   });
   *
   *   tracker.track('button.clicked', {
   *     properties: { label: 'export_csv', format: 'csv' },
   *   });
   *
   *   tracker.track('nav.clicked', { properties: { destination: '/pipelines' } });
   */
  track(event_type: string, opts: TrackOptions = {}): void {
    try {
      const event: BufferedEvent = {
        event_id: generateEventId(),
        event_type,
        ts_ms: Date.now(),
      };
      if (opts.entity_id !== undefined) event.entity_id = opts.entity_id;
      if (opts.duration_ms !== undefined) event.duration_ms = opts.duration_ms;
      if (opts.event_category !== undefined) event.event_category = opts.event_category;
      if (opts.properties !== undefined && Object.keys(opts.properties).length > 0) {
        event.properties = opts.properties;
      }

      this.buffer.push(event);

      // Soft back-pressure: cap buffer size to prevent memory runaway if
      // the backend is down for hours.
      if (this.buffer.length > MAX_BUFFER_SIZE) {
        this.buffer.splice(0, this.buffer.length - MAX_BUFFER_SIZE);
      }

      if (this.buffer.length >= MAX_BATCH_SIZE) {
        this.scheduleFlush(0);
      } else if (!this.flushTimer) {
        this.flushTimer = setTimeout(() => this.scheduleFlush(), MAX_BATCH_AGE_MS);
      }
    } catch {
      // Never let a bad track() call crash the page.
    }
  }

  /**
   * Request a flush. Runs during idle time to avoid jank, never awaits.
   * Wrapped in try/catch for extra paranoia — setTimeout/clearTimeout
   * shouldn't throw, but if some test shim or exotic runtime makes them
   * throw, we swallow rather than crash.
   *
   * Bursty track() calls (e.g. 50 events in a tick) would otherwise enqueue
   * one idle callback per call. The `idleFlushPending` guard collapses all
   * of those into a single pending drain — it clears at the top of
   * `doFlush()` so the next burst can enqueue again.
   */
  private scheduleFlush(delayMs?: number): void {
    try {
      if (this.flushTimer) {
        clearTimeout(this.flushTimer);
        this.flushTimer = null;
      }
      if (typeof delayMs === 'number' && delayMs > 0) {
        this.flushTimer = setTimeout(() => {
          if (this.idleFlushPending) return;
          this.idleFlushPending = true;
          scheduleIdle(() => this.doFlush());
        }, delayMs);
      } else {
        if (this.idleFlushPending) return;
        this.idleFlushPending = true;
        scheduleIdle(() => this.doFlush());
      }
    } catch {
      // Never crash — tracking is best-effort.
    }
  }

  /**
   * Drain the buffer into a POST request. Fire-and-forget.
   * `keepalive: true` lets the request survive page unload.
   */
  private doFlush(): void {
    // Clear the pending-flush guard so the next burst of track() calls can
    // enqueue a fresh idle callback. Do this BEFORE the empty-buffer check
    // so we don't leave the guard stuck on when there's nothing to send.
    this.idleFlushPending = false;
    if (this.buffer.length === 0) return;
    const events = this.buffer.splice(0);
    try {
      void fetch(ENDPOINT, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ events }),
        keepalive: true,
      }).catch(() => {
        // swallow — never block UI, never crash, never retry
      });
    } catch {
      // fetch() itself can synchronously throw in some edge cases
      // (e.g. CSP violation, very old browser). Swallow it.
    }
  }

  /**
   * Public flush — force an immediate drain. Use this when you need a
   * best-effort guarantee that buffered events reach the server *before*
   * you clear the session (e.g. logout), since SPA navigation won't fire
   * `beforeunload` and the logout POST will clear the auth cookie.
   *
   * Still fire-and-forget — returns synchronously, never throws, never
   * awaits. `keepalive: true` on the POST lets it survive if the user
   * also navigates away immediately after.
   */
  flush(): void {
    try {
      if (this.flushTimer) {
        clearTimeout(this.flushTimer);
        this.flushTimer = null;
      }
      this.doFlush();
    } catch {
      // Never crash — tracking is best-effort.
    }
  }

  /**
   * Force a synchronous-ish flush. Called on visibility change + unload.
   * Uses `keepalive: true` so the request completes even if the page
   * is navigating away. Wrapped in try/catch for extra paranoia.
   */
  private flushNow(): void {
    try {
      if (this.flushTimer) {
        clearTimeout(this.flushTimer);
        this.flushTimer = null;
      }
      this.doFlush();
    } catch {
      // Never crash on unload.
    }
  }

  /**
   * Attach visibility / unload flush hooks. Safe to call multiple times.
   * Must be called once from app root (e.g. in PortalLayout's useEffect).
   */
  init(): void {
    if (this.initialized) return;
    this.initialized = true;
    try {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
          this.flushNow();
        }
      });
      window.addEventListener('beforeunload', () => {
        this.flushNow();
      });
      window.addEventListener('pageshow', (e) => {
        if (!e.persisted && this.buffer.length > 0) {
          this.scheduleFlush(0);
        }
      });
    } catch {
      // If document / window aren't available (SSR, test env), skip.
    }
  }
}

/** Singleton — shared across the app. */
export const tracker = new EventTracker();
